import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Database, Download, CheckCircle, AlertCircle, Users, MessageSquare, FileText, ImageIcon, Heart } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminSeedData() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [fixingImages, setFixingImages] = useState(false);
  const [status, setStatus] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [fixResult, setFixResult] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/api/admin/seed-content/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching status:', error);
    }
  };

  const handleImport = async () => {
    if (!window.confirm('Voulez-vous importer les données de contenu ? Les données existantes ne seront pas écrasées.')) {
      return;
    }

    setLoading(true);
    setImportResult(null);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/api/admin/seed-content`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setImportResult(response.data.results);
      toast.success('Import terminé avec succès !');
      fetchStatus();
    } catch (error) {
      console.error('Error importing:', error);
      toast.error('Erreur lors de l\'import');
    } finally {
      setLoading(false);
    }
  };

  const handleFixImages = async () => {
    if (!window.confirm('Voulez-vous corriger les URLs des images pour la production ?')) {
      return;
    }

    setFixingImages(true);
    setFixResult(null);
    
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_URL}/api/admin/fix-image-urls`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFixResult(response.data.results);
      toast.success('URLs des images corrigées !');
    } catch (error) {
      console.error('Error fixing images:', error);
      toast.error('Erreur lors de la correction des images');
    } finally {
      setFixingImages(false);
    }
  };

  const StatCard = ({ icon: Icon, title, current, available, color }) => (
    <div className={`bg-${color}-50 border border-${color}-200 rounded-lg p-4`}>
      <div className="flex items-center gap-3">
        <div className={`p-2 bg-${color}-100 rounded-lg`}>
          <Icon className={`h-5 w-5 text-${color}-600`} />
        </div>
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className="text-xl font-bold">{current} <span className="text-sm font-normal text-gray-500">/ {available} disponibles</span></p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="admin-seed-data">
      <div>
        <h1 className="text-2xl font-bold">Import des Données</h1>
        <p className="text-gray-600">Importer les données de contenu pour la production</p>
      </div>

      {/* Status Cards */}
      {status && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <MessageSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Témoignages</p>
                <p className="text-xl font-bold">{status.testimonials} <span className="text-sm font-normal text-gray-500">/ {status.seed_available.testimonials} dispo</span></p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Users className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Équipe</p>
                <p className="text-xl font-bold">{status.team} <span className="text-sm font-normal text-gray-500">/ {status.seed_available.team} dispo</span></p>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <FileText className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Articles Blog</p>
                <p className="text-xl font-bold">{status.blog_posts} <span className="text-sm font-normal text-gray-500">/ {status.seed_available.blog_posts} dispo</span></p>
              </div>
            </div>
          </div>

          <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-pink-100 rounded-lg">
                <Heart className="h-5 w-5 text-pink-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Retour d'expériences</p>
                <p className="text-xl font-bold">{status.experiences || 0} <span className="text-sm font-normal text-gray-500">/ {status.seed_available?.experiences || 34} dispo</span></p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fix Images Card */}
      <Card className="border-blue-200 bg-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ImageIcon className="h-5 w-5 text-blue-600" />
            Corriger les URLs des Images
          </CardTitle>
          <CardDescription>
            <strong>IMPORTANT pour la production :</strong> Remplace les URLs locales par des images Unsplash accessibles partout.
            Utilisez ce bouton après un déploiement en production si les images ne s'affichent pas.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={handleFixImages} 
            disabled={fixingImages}
            size="lg"
            variant="outline"
            className="w-full md:w-auto border-blue-500 text-blue-700 hover:bg-blue-100"
            data-testid="fix-images-button"
          >
            {fixingImages ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                Correction en cours...
              </>
            ) : (
              <>
                <ImageIcon className="h-4 w-4 mr-2" />
                Corriger les Images
              </>
            )}
          </Button>

          {/* Fix Results */}
          {fixResult && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200">
              <h3 className="font-semibold mb-3 flex items-center gap-2 text-blue-700">
                <CheckCircle className="h-5 w-5" />
                Images corrigées
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Équipe:</span>
                  <span className="font-medium">{fixResult.team} corrigées</span>
                </div>
                <div className="flex justify-between">
                  <span>Hero Slides:</span>
                  <span className="font-medium">{fixResult.hero_slides} corrigées</span>
                </div>
                <div className="flex justify-between">
                  <span>Galerie:</span>
                  <span className="font-medium">{fixResult.gallery} corrigées</span>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Import Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Importer le Contenu
          </CardTitle>
          <CardDescription>
            Cette action importera les témoignages, membres de l'équipe, articles de blog et <strong>34 retours d'expériences</strong>.
            Les données existantes ne seront pas écrasées (pas de doublons).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            onClick={handleImport} 
            disabled={loading}
            size="lg"
            className="w-full md:w-auto"
            data-testid="import-button"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Import en cours...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Importer les Données
              </>
            )}
          </Button>

          {/* Import Results */}
          {importResult && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Résultat de l'import
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Témoignages:</span>
                  <span className="font-medium">
                    {importResult.testimonials.imported} importés, {importResult.testimonials.skipped} ignorés
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Équipe:</span>
                  <span className="font-medium">
                    {importResult.team.imported} importés, {importResult.team.skipped} ignorés
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Articles Blog:</span>
                  <span className="font-medium">
                    {importResult.blog_posts.imported} importés, {importResult.blog_posts.skipped} ignorés
                  </span>
                </div>
                {importResult.experiences && (
                  <div className="flex justify-between">
                    <span>Retour d'expériences:</span>
                    <span className="font-medium text-pink-600">
                      {importResult.experiences.imported} importés, {importResult.experiences.skipped} ignorés
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800">Information</h3>
              <p className="text-sm text-amber-700 mt-1">
                Cette fonctionnalité est destinée à initialiser une nouvelle base de données de production 
                avec du contenu de démonstration. Les données existantes ne seront jamais supprimées ou modifiées.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
